import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Trees, Heart, Euro, CheckCircle, Clock, AlertCircle } from "lucide-react";
import { format, isAfter } from 'date-fns';
import { de } from 'date-fns/locale';

interface SocialObligation {
  id: string;
  tenant_id: string;
  social_project_id: string;
  required_units: number;
  fulfilled_units: number;
  cost_per_unit: number;
  total_cost: number;
  status: string;
  due_date: string | null;
  created_at: string;
  updated_at: string;
  social_projects: {
    name: string;
    project_type: string;
  } | null;
}

interface AdminObligationsDashboardProps {
  tenantId: string;
}

export function AdminObligationsDashboard({ tenantId }: AdminObligationsDashboardProps) {
  const [obligations, setObligations] = useState<SocialObligation[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (tenantId) {
      fetchObligations();
    }
  }, [tenantId]);

  const fetchObligations = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_social_obligations')
        .select(`
          *,
          social_projects (
            name,
            project_type
          )
        `)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const validObligations = (data || [])
        .filter(item => 
          item.social_projects && 
          typeof item.social_projects === 'object' && 
          item.social_projects !== null && 
          'name' in (item.social_projects || {}) &&
          'project_type' in (item.social_projects || {})
        ) as unknown as SocialObligation[];
      setObligations(validObligations);
    } catch (error) {
      console.error('Error fetching obligations:', error);
      console.error('Full error details:', error);
      toast({
        title: "Fehler beim Laden",
        description: `Verpflichtungen konnten nicht geladen werden: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsFulfilled = async (obligationId: string, requiredUnits: number, costPerUnit: number) => {
    setProcessing(obligationId);
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Nicht angemeldet');

      // Create action record
      const { error: actionError } = await supabase
        .from('admin_social_actions')
        .insert({
          tenant_id: tenantId,
          social_project_id: obligations.find(o => o.id === obligationId)?.social_project_id,
          obligation_id: obligationId,
          units_completed: requiredUnits,
          amount_spent: requiredUnits * costPerUnit,
          description: 'Als erledigt markiert',
          created_by_user_id: user.user.id,
        });

      if (actionError) throw actionError;

      toast({
        title: "Verpflichtung erfüllt",
        description: "Die Verpflichtung wurde als erledigt markiert.",
      });

      // Refresh obligations
      await fetchObligations();
    } catch (error) {
      console.error('Error marking as fulfilled:', error);
      toast({
        title: "Fehler",
        description: "Die Verpflichtung konnte nicht als erledigt markiert werden.",
        variant: "destructive",
      });
    } finally {
      setProcessing(null);
    }
  };

  const getProjectIcon = (projectType: string) => {
    switch (projectType) {
      case 'trees':
        return <Trees className="h-5 w-5 text-green-600" />;
      case 'families':
      case 'family_help':
        return <Heart className="h-5 w-5 text-red-600" />;
      default:
        return <Euro className="h-5 w-5 text-blue-600" />;
    }
  };

  const getStatusInfo = (obligation: SocialObligation) => {
    const isOverdue = obligation.due_date && isAfter(new Date(), new Date(obligation.due_date));
    const progress = (obligation.fulfilled_units / obligation.required_units) * 100;

    if (obligation.status === 'fulfilled') {
      return {
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        icon: <CheckCircle className="h-4 w-4" />,
        badge: <Badge variant="secondary" className="bg-green-100 text-green-800">Erfüllt</Badge>
      };
    } else if (isOverdue) {
      return {
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: <AlertCircle className="h-4 w-4" />,
        badge: <Badge variant="destructive">Überfällig</Badge>
      };
    } else {
      return {
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        icon: <Clock className="h-4 w-4" />,
        badge: <Badge variant="outline">Offen</Badge>
      };
    }
  };

  const getUnitName = (projectType: string) => {
    switch (projectType) {
      case 'trees':
        return 'Bäume';
      case 'families':
      case 'family_help':
        return 'Familien';
      default:
        return 'Einheiten';
    }
  };

  if (loading) {
    return <div className="p-4">Lade Verpflichtungen...</div>;
  }

  const pendingObligations = obligations.filter(o => o.status !== 'fulfilled');
  const totalOutstanding = pendingObligations.reduce((sum, o) => sum + (o.total_cost - (o.fulfilled_units * o.cost_per_unit)), 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Offene Verpflichtungen</p>
                <p className="text-2xl font-bold">{pendingObligations.length}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ausstehender Betrag</p>
                <p className="text-2xl font-bold">{totalOutstanding.toFixed(2)}€</p>
              </div>
              <Euro className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Erfüllte Verpflichtungen</p>
                <p className="text-2xl font-bold">{obligations.filter(o => o.status === 'fulfilled').length}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Obligations List */}
      <Card>
        <CardHeader>
          <CardTitle>Ihre To-Do Liste</CardTitle>
          <CardDescription>
            Basierend auf den Beiträgen Ihrer Kunden zu sozialen Projekten
          </CardDescription>
        </CardHeader>
        <CardContent>
          {obligations.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Noch keine Verpflichtungen vorhanden. Sobald Kunden zu sozialen Projekten beitragen, 
              werden hier Ihre entsprechenden Verpflichtungen angezeigt.
            </p>
          ) : (
            <div className="space-y-4">
              {obligations.map((obligation) => {
                const status = getStatusInfo(obligation);
                const progress = (obligation.fulfilled_units / obligation.required_units) * 100;
                const remainingUnits = obligation.required_units - obligation.fulfilled_units;
                const remainingCost = remainingUnits * obligation.cost_per_unit;

                return (
                  <div
                    key={obligation.id}
                    className={`p-4 border rounded-lg ${status.bgColor} ${status.borderColor}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {getProjectIcon(obligation.social_projects?.project_type || 'default')}
                        <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium">{obligation.social_projects?.name}</h4>
                          {status.badge}
                        </div>
                          
                          <div className="space-y-2">
                            <p className="text-sm">
                              <span className="font-medium">{obligation.required_units} {getUnitName(obligation.social_projects?.project_type || 'default')}</span>
                              {' '}({obligation.total_cost.toFixed(2)}€) - {' '}
                              <span className="text-green-600">{obligation.fulfilled_units} erfüllt</span>
                              {remainingUnits > 0 && (
                                <span className={status.color}>, {remainingUnits} offen ({remainingCost.toFixed(2)}€)</span>
                              )}
                            </p>
                            
                            <Progress value={progress} className="w-full h-2" />
                            
                            {obligation.due_date && (
                              <p className="text-xs text-muted-foreground">
                                Fällig am {format(new Date(obligation.due_date), 'dd.MM.yyyy', { locale: de })}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {obligation.status !== 'fulfilled' && remainingUnits > 0 && (
                        <Button
                          size="sm"
                          onClick={() => markAsFulfilled(obligation.id, remainingUnits, obligation.cost_per_unit)}
                          disabled={processing === obligation.id}
                        >
                          {processing === obligation.id ? "Verarbeite..." : "Als erledigt markieren"}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}